import { useState, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Capacitor } from "@capacitor/core";
import "./CancelFlowModal.css";

const CANCEL_REASONS = [
  { id: "too_expensive", label: "It's too expensive" },
  { id: "not_using", label: "I'm not using it enough" },
  { id: "missing_feature", label: "Missing a feature I need" },
  { id: "switching", label: "Switching to another tool" },
  { id: "technical", label: "Technical issues / bugs" },
  { id: "temporary", label: "I only needed it temporarily" },
  { id: "other", label: "Other" },
];

const OFFER_CONFIG = {
  too_expensive: {
    type: "discount",
    icon: "💰",
    title: "How about 25% off for the next 3 months?",
    description: "We'd love to keep you. Here's a special offer just for you.",
    savingsText: "Save $9.75 over the next 3 months",
    acceptLabel: "Accept 25% Off",
    fallbackText: "That's less than $10/month — unlimited SVG exports, AI tools, and commercial license.",
  },
  not_using: {
    type: "pause",
    icon: "⏸️",
    title: "Take a break instead?",
    description: "Pause your subscription for up to 3 months. Your files and settings stay safe, and you can unpause anytime.",
    savingsText: "No charges while paused",
    acceptLabel: "Pause for 1 Month",
    fallbackText: null,
  },
  missing_feature: {
    type: "discount",
    icon: "🛠️",
    title: "We're always improving",
    description: "We ship new features every month. Stick around with 25% off for 3 months and see what's coming.",
    savingsText: "Save $9.75 while we build what you need",
    acceptLabel: "Accept 25% Off",
    fallbackText: null,
  },
  switching: {
    type: "discount",
    icon: "🤝",
    title: "Give us another chance?",
    description: "SvgTrace is the only tool with color-layered SVGs and AI-powered tracing built in. Stay for 25% off.",
    savingsText: "Save $9.75 over the next 3 months",
    acceptLabel: "Accept 25% Off",
    fallbackText: null,
  },
  technical: {
    type: "support",
    icon: "🔧",
    title: "Let us fix that for you",
    description: "We're sorry about the issues. Our team can help resolve them — reach out and we'll prioritize your case.",
    savingsText: null,
    acceptLabel: "Contact Support",
    fallbackText: "Email support@svgtrace.com and we'll get back to you within 24 hours.",
  },
  temporary: {
    type: "pause",
    icon: "⏸️",
    title: "Pause instead of canceling?",
    description: "If you might need SvgTrace again, pause your subscription. No charges, and you can resume anytime.",
    savingsText: "No charges while paused",
    acceptLabel: "Pause for 1 Month",
    fallbackText: null,
  },
  other: {
    type: "discount",
    icon: "💜",
    title: "We'd hate to see you go",
    description: "How about 25% off for the next 3 months while we work on making SvgTrace even better?",
    savingsText: "Save $9.75 over the next 3 months",
    acceptLabel: "Accept 25% Off",
    fallbackText: null,
  },
};

const STEPS = { SURVEY: 0, OFFER: 1, CONFIRM: 2, DONE: 3 };

const CancelFlowModal = ({ isOpen, onClose, onCanceled, isIOSSub }) => {
  const [step, setStep] = useState(STEPS.SURVEY);
  const [selectedReason, setSelectedReason] = useState(null);
  const [freeText, setFreeText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [pauseMonths, setPauseMonths] = useState(1);

  const reset = useCallback(() => {
    setStep(STEPS.SURVEY);
    setSelectedReason(null);
    setFreeText("");
    setLoading(false);
    setResult(null);
    setPauseMonths(1);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const callCancelFlow = useCallback(
    async (action, extra = {}) => {
      const functions = getFunctions();
      const fn = httpsCallable(functions, "cancelSubscriptionFlow");
      return fn({
        action,
        reason: selectedReason,
        reasonText: freeText || undefined,
        ...extra,
      });
    },
    [selectedReason, freeText]
  );

  const handleContinueToOffer = () => {
    if (!selectedReason) return;
    if (isIOSSub) {
      setStep(STEPS.CONFIRM);
      return;
    }
    setStep(STEPS.OFFER);
  };

  const handleAcceptOffer = async () => {
    const offer = OFFER_CONFIG[selectedReason];
    if (!offer) return;

    if (offer.type === "support") {
      window.open("mailto:support@svgtrace.com?subject=Technical Issue", "_blank");
      handleClose();
      return;
    }

    if (isIOSSub) {
      setResult({
        outcome: "ios-redirect",
        message: "To apply changes, manage your subscription in your Apple ID settings.",
      });
      setStep(STEPS.DONE);
      return;
    }

    setLoading(true);
    try {
      const action = offer.type === "pause" ? "pause" : "applyDiscount";
      const extra = offer.type === "pause" ? { pauseMonths } : {};
      const { data } = await callCancelFlow(action, extra);
      setResult(data);
      setStep(STEPS.DONE);
    } catch (err) {
      console.error("Save offer error:", err);
      setResult({ outcome: "error", message: "Something went wrong. Please try again." });
      setStep(STEPS.DONE);
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineOffer = () => {
    setStep(STEPS.CONFIRM);
  };

  const handleConfirmCancel = async () => {
    if (isIOSSub) {
      if (Capacitor.isNativePlatform()) {
        window.open("https://apps.apple.com/account/subscriptions", "_system");
      } else {
        window.open("https://apps.apple.com/account/subscriptions", "_blank");
      }

      const functions = getFunctions();
      const fn = httpsCallable(functions, "cancelSubscriptionFlow");
      try {
        await fn({
          action: "cancel",
          reason: selectedReason,
          reasonText: freeText || undefined,
        });
      } catch (_) {}

      setResult({
        outcome: "ios-redirect",
        message: "Complete your cancellation in the App Store.",
      });
      setStep(STEPS.DONE);
      return;
    }

    setLoading(true);
    try {
      const { data } = await callCancelFlow("cancel");
      setResult(data);
      setStep(STEPS.DONE);
      if (onCanceled) onCanceled(data);
    } catch (err) {
      console.error("Cancel error:", err);
      setResult({ outcome: "error", message: "Something went wrong. Please try again." });
      setStep(STEPS.DONE);
    } finally {
      setLoading(false);
    }
  };

  const handleKeepSubscription = () => {
    handleClose();
  };

  if (!isOpen) return null;

  const offer = selectedReason ? OFFER_CONFIG[selectedReason] : null;
  const totalSteps = 3;
  const currentStep = Math.min(step, 2);

  return (
    <div className="cancel-flow-overlay" onClick={handleClose}>
      <div className="cancel-flow-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cancel-flow-modal__header">
          <h3 className="cancel-flow-modal__title">
            {step === STEPS.SURVEY && "Help us improve"}
            {step === STEPS.OFFER && "Before you go..."}
            {step === STEPS.CONFIRM && "Confirm cancellation"}
            {step === STEPS.DONE && (result?.outcome === "canceled" || result?.outcome === "ios-redirect" ? "You're all set" : "Great news!")}
          </h3>
          <button className="cancel-flow-modal__close" onClick={handleClose}>
            ×
          </button>
        </div>

        {step < STEPS.DONE && (
          <div className="cancel-flow__steps">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`cancel-flow__step-dot${
                  i === currentStep ? " cancel-flow__step-dot--active" : ""
                }${i < currentStep ? " cancel-flow__step-dot--done" : ""}`}
              />
            ))}
          </div>
        )}

        <div className="cancel-flow-modal__body">
          {step === STEPS.SURVEY && (
            <>
              <p className="cancel-flow-modal__subtitle">
                What's the main reason you're thinking about canceling?
              </p>
              <div className="cancel-flow__reasons">
                {CANCEL_REASONS.map((reason) => (
                  <button
                    key={reason.id}
                    className={`cancel-flow__reason${
                      selectedReason === reason.id ? " cancel-flow__reason--selected" : ""
                    }`}
                    onClick={() => setSelectedReason(reason.id)}
                  >
                    <span className="cancel-flow__reason-radio" />
                    {reason.label}
                  </button>
                ))}
              </div>
              {selectedReason && (
                <textarea
                  className="cancel-flow__freetext"
                  placeholder="Anything else you'd like to share? (optional)"
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  rows={2}
                />
              )}
              <div className="cancel-flow__actions">
                <button
                  className="cancel-flow__btn cancel-flow__btn--primary"
                  disabled={!selectedReason}
                  onClick={handleContinueToOffer}
                >
                  Continue
                </button>
                <button className="cancel-flow__btn cancel-flow__btn--ghost" onClick={handleClose}>
                  Never mind, keep my subscription
                </button>
              </div>
            </>
          )}

          {step === STEPS.OFFER && offer && (
            <>
              <div className="cancel-flow__offer-card">
                <span className="cancel-flow__offer-icon">{offer.icon}</span>
                <h4 className="cancel-flow__offer-title">{offer.title}</h4>
                <p className="cancel-flow__offer-desc">{offer.description}</p>
                {offer.savingsText && (
                  <p className="cancel-flow__offer-savings">{offer.savingsText}</p>
                )}
              </div>
              {offer.type === "pause" && (
                <div className="cancel-flow__pause-picker">
                  <p className="cancel-flow__pause-picker-label">How long would you like to pause?</p>
                  <div className="cancel-flow__pause-options">
                    {[1, 2, 3].map((m) => (
                      <button
                        key={m}
                        className={`cancel-flow__pause-option${pauseMonths === m ? " cancel-flow__pause-option--selected" : ""}`}
                        onClick={() => setPauseMonths(m)}
                      >
                        {m} {m === 1 ? "month" : "months"}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {offer.fallbackText && (
                <p className="cancel-flow__offer-fallback">{offer.fallbackText}</p>
              )}
              <div className="cancel-flow__actions">
                <button
                  className="cancel-flow__btn cancel-flow__btn--accept"
                  onClick={handleAcceptOffer}
                  disabled={loading}
                >
                  {loading ? <span className="cancel-flow__spinner" /> : (offer.type === "pause" ? `Pause for ${pauseMonths} ${pauseMonths === 1 ? "Month" : "Months"}` : offer.acceptLabel)}
                </button>
                <button
                  className="cancel-flow__btn cancel-flow__btn--danger"
                  onClick={handleDeclineOffer}
                  disabled={loading}
                >
                  No thanks, continue canceling
                </button>
                <button className="cancel-flow__btn cancel-flow__btn--ghost" onClick={handleKeepSubscription}>
                  Never mind, keep my subscription
                </button>
              </div>
            </>
          )}

          {step === STEPS.CONFIRM && (
            <>
              <div className="cancel-flow__confirm-card">
                <h4 className="cancel-flow__confirm-title">Are you sure?</h4>
                <p className="cancel-flow__confirm-desc">
                  Your subscription will be canceled at the end of your current billing period.
                  You'll keep full access until then, and you can resubscribe anytime.
                </p>
              </div>
              <p className="cancel-flow-modal__subtitle">
                You'll lose access to unlimited SVG exports, AI tools, the built-in editor, and your commercial license.
              </p>
              <div className="cancel-flow__actions">
                <button
                  className="cancel-flow__btn cancel-flow__btn--keep"
                  onClick={handleKeepSubscription}
                >
                  Keep My Subscription
                </button>
                <button
                  className="cancel-flow__btn cancel-flow__btn--danger"
                  onClick={handleConfirmCancel}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="cancel-flow__spinner" style={{ borderColor: "rgba(220,38,38,0.3)", borderTopColor: "#dc2626" }} />
                      Canceling...
                    </>
                  ) : (
                    "Cancel My Subscription"
                  )}
                </button>
              </div>
            </>
          )}

          {step === STEPS.DONE && result && (
            <>
              {result.outcome === "saved-discount" && (
                <div className="cancel-flow__success">
                  <span className="cancel-flow__success-icon">🎉</span>
                  <h4 className="cancel-flow__success-title">Discount applied!</h4>
                  <p className="cancel-flow__success-desc">
                    You'll get 25% off for the next 3 months. We're glad you're staying!
                  </p>
                </div>
              )}
              {result.outcome === "saved-pause" && (
                <div className="cancel-flow__success">
                  <span className="cancel-flow__success-icon">⏸️</span>
                  <h4 className="cancel-flow__success-title">Subscription paused</h4>
                  <p className="cancel-flow__success-desc">
                    Your subscription is paused. You won't be charged until it resumes.
                    {result.resumesAt && ` It will resume on ${new Date(result.resumesAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}.`}
                  </p>
                </div>
              )}
              {result.outcome === "canceled" && (
                <div className="cancel-flow__success">
                  <span className="cancel-flow__success-icon">👋</span>
                  <h4 className="cancel-flow__success-title">Subscription canceled</h4>
                  <p className="cancel-flow__success-desc">
                    You'll keep access until{" "}
                    {result.accessUntil
                      ? new Date(result.accessUntil).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                      : "the end of your billing period"}
                    . You can resubscribe anytime.
                  </p>
                </div>
              )}
              {result.outcome === "ios-redirect" && (
                <div className="cancel-flow__success">
                  <span className="cancel-flow__success-icon">📱</span>
                  <h4 className="cancel-flow__success-title">Thanks for the feedback</h4>
                  <p className="cancel-flow__success-desc">{result.message}</p>
                </div>
              )}
              {result.outcome === "error" && (
                <div className="cancel-flow__success">
                  <span className="cancel-flow__success-icon">⚠️</span>
                  <h4 className="cancel-flow__success-title">Something went wrong</h4>
                  <p className="cancel-flow__success-desc">{result.message}</p>
                </div>
              )}
              <div className="cancel-flow__actions">
                <button className="cancel-flow__btn cancel-flow__btn--primary" onClick={handleClose}>
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CancelFlowModal;
